import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { BLOCK_META } from './blocks/registry';
import type { BlockId, CustomBlockConfig } from './blocks/types';
import { defaultCustomLayout } from './variants/presets';

interface Props {
  open: boolean;
  layout: CustomBlockConfig[];
  onClose: () => void;
  onSave: (next: CustomBlockConfig[]) => void;
}

interface RowProps {
  config: CustomBlockConfig;
  onToggle: (id: BlockId) => void;
}

const SortableRow = ({ config, onToggle }: RowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: config.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const meta = BLOCK_META[config.id];
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={[
        'flex items-start gap-2 rounded-md border px-2 py-2 transition-colors',
        config.visible
          ? 'border-line bg-bg-primary'
          : 'border-dashed border-line bg-bg-secondary opacity-60',
      ].join(' ')}
    >
      <button
        type="button"
        aria-label={`Drag ${meta.label}`}
        {...attributes}
        {...listeners}
        className="grid h-9 w-7 shrink-0 cursor-grab touch-none place-items-center text-text-tertiary hover:text-text-primary"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-text-primary">{meta.label}</div>
        <div className="line-clamp-2 text-[11px] text-text-tertiary">{meta.description}</div>
      </div>
      <button
        type="button"
        onClick={() => onToggle(config.id)}
        aria-label={config.visible ? `Hide ${meta.label}` : `Show ${meta.label}`}
        className={[
          'grid h-9 w-9 shrink-0 place-items-center rounded-md transition-colors',
          config.visible
            ? 'text-status-healthy hover:bg-status-healthy/10'
            : 'text-text-tertiary hover:bg-bg-tertiary hover:text-text-primary',
        ].join(' ')}
      >
        {config.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      </button>
    </li>
  );
};

export const CustomLayoutDrawer = ({ open, layout, onClose, onSave }: Props) => {
  const [draft, setDraft] = useState<CustomBlockConfig[]>(layout);

  useEffect(() => {
    if (open) setDraft(layout);
  }, [open, layout]);

  // Lock scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Blocks already in draft, plus any not yet added (so user can add later
  // ones). We dedupe by id; new blocks default to hidden.
  const allBlocks = useMemo<CustomBlockConfig[]>(() => {
    const seen = new Set(draft.map((b) => b.id));
    const extras: CustomBlockConfig[] = (Object.keys(BLOCK_META) as BlockId[])
      .filter((id) => !seen.has(id))
      .map((id) => ({ id, visible: false }));
    return [...draft, ...extras];
  }, [draft]);

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = allBlocks.map((b) => b.id);
    const oldIdx = ids.indexOf(active.id as BlockId);
    const newIdx = ids.indexOf(over.id as BlockId);
    if (oldIdx < 0 || newIdx < 0) return;
    setDraft(arrayMove(allBlocks, oldIdx, newIdx));
  };

  const toggle = (id: BlockId) => {
    setDraft((prev) => {
      const exists = prev.find((b) => b.id === id);
      if (exists) {
        return prev.map((b) => (b.id === id ? { ...b, visible: !b.visible } : b));
      }
      // Adding for the first time
      return [...prev, { id, visible: true }];
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-labelledby="customize-title"
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-bg-primary shadow-xl sm:rounded-l-2xl"
      >
        <header className="flex items-center justify-between border-b border-line p-4">
          <div>
            <h2 id="customize-title" className="text-sm font-semibold tracking-tight text-text-primary">
              Customize your dashboard
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary">
              Drag to reorder. Click the eye to show or hide.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-9 w-9 place-items-center rounded-md text-text-secondary hover:bg-bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={allBlocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <ul className="space-y-1.5">
                {allBlocks.map((b) => (
                  <SortableRow key={b.id} config={b} onToggle={toggle} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-line bg-bg-secondary p-3">
          <button
            type="button"
            onClick={() => setDraft(defaultCustomLayout())}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-bg-tertiary"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset to Cozy
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-bg-tertiary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                onSave(draft);
                onClose();
              }}
              className="rounded-md bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-orange-dark"
            >
              Save
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
};
